import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, AlertTriangle, MousePointerClick, Receipt, Lock } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useConsolidatedBusiness } from '../hooks/useConsolidatedBusiness';
import { ConsBusinessStatsBar } from '../components/consolidated-business/ConsBusinessStats';
import { ConsBusinessFiltersBar } from '../components/consolidated-business/ConsBusinessFilters';
import { ConsBusinessTable } from '../components/consolidated-business/ConsBusinessTable';
import { ConsBusinessDetailDrawer } from '../components/consolidated-business/ConsBusinessDetailDrawer';
import { ConsBusinessStatusModal } from '../components/consolidated-business/ConsBusinessStatusModal';
import { ConsBusinessTransactionsModal } from '../components/consolidated-business/ConsBusinessTransactionsModal';
import { CB_STATUS_META, CB_STATUS_ORDER, toCbStatus } from '../components/consolidated-business/status';
import { ContextMenu } from '../components/ui/ContextMenu';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { statusChangeQuestion } from '../components/consolidated-business/statusConfirm';
import { KpiModeToggle } from '../components/consolidated-business/KpiModeToggle';
import { fmtDate } from '../components/consolidated-business/format';
import { buildGroupKey } from '../types/consolidatedBusiness';
import type { ConsBusinessRow, StatusSetPayload, CbStatus } from '../types/consolidatedBusiness';
import { consolidatedBusinessApi } from '../services/consolidatedBusinessApi';
import { useToast } from '../components/ui/Toast';
import {
    DEFAULT_FILTERS, DEFAULT_KPI_MODE, DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER, writeListState,
} from '../components/consolidated-business/urlState';

const LIST_STATE_DEFAULTS = {
    filters: DEFAULT_FILTERS, sortBy: DEFAULT_SORT_FIELD, sortOrder: DEFAULT_SORT_ORDER, kpiMode: DEFAULT_KPI_MODE, page: 1,
};

// `-v2` since the design audit of 2026-10-07 (S2-04): the default went from 60 % to 50 % so the
// compact table keeps its five columns at 1280 px, and a stored 60 would have hidden that.
const DRAWER_WIDTH_KEY = 'consbusiness-drawer-width-v2';
const DEFAULT_DRAWER_WIDTH = 50;

function rowKey(row: ConsBusinessRow): string {
    return `${row.CustomerID}|${row.ProductName}`;
}

export function ConsolidatedBusinessPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/consolidated-business');
    const { toast } = useToast();
    const {
        items, total, loading, error, stats, filterOptions,
        filters, kpiMode, sortBy, sortOrder, page, limit,
        setFilters, setKpiMode, setSort, setPage, refresh,
    } = useConsolidatedBusiness();

    const [selectedRow, setSelectedRow] = useState<ConsBusinessRow | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();

    // The list state (filters, sort, KPI mode, page) lives in the URL so a view can be shared or
    // reloaded (design audit S3-04). Always `replace` — only opening the drawer, below, gets its
    // own history entry. `useConsolidatedBusiness` reads the matching URL on first mount.
    useEffect(() => {
        const next = writeListState(searchParams, { filters, sortBy, sortOrder, kpiMode, page }, LIST_STATE_DEFAULTS);
        if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, sortBy, sortOrder, kpiMode, page]);

    // The open drawer follows `?open=<groupKey>` the other way: opening pushes a history entry
    // (Roman, 2026-10-07 — Back should close the drawer before it leaves the page), so this
    // effect only ever REACTS to the URL — the browser's Back/Forward, or a deep link/reload —
    // never writes it itself. `openDrawer`/`closeDrawer` below write the URL directly.
    const openGroupKey = searchParams.get('open');
    useEffect(() => {
        if (!openGroupKey) { setSelectedRow(null); return; }
        setSelectedRow(prev => {
            if (prev && buildGroupKey(prev) === openGroupKey) return prev;
            return items.find(it => buildGroupKey(it) === openGroupKey) ?? prev;
        });
    }, [openGroupKey, items]);

    const openDrawer = useCallback((row: ConsBusinessRow) => {
        setSelectedRow(row);
        const next = new URLSearchParams(searchParams);
        next.set('open', buildGroupKey(row));
        setSearchParams(next, { replace: false });
    }, [searchParams, setSearchParams]);

    const closeDrawer = useCallback(() => {
        setSelectedRow(null);
        const next = new URLSearchParams(searchParams);
        next.delete('open');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const [writeBusy, setWriteBusy] = useState(false);
    const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);
    // Status menu opened from the list's Status cell, and the dialog it can lead to.
    const [statusMenu, setStatusMenu] = useState<{ x: number; y: number; row: ConsBusinessRow } | null>(null);
    const [statusModal, setStatusModal] = useState<
        { row: ConsBusinessRow; target: 'Checked' | 'InProgress' } | null>(null);
    // Right-click drill-down. The menu and the modal are separate state: the menu closes as soon
    // as the item is picked, the modal outlives it. Both live here rather than in the table, which
    // is a pure presenter shared with the Customer-360 Lost-Business tab.
    const [txMenu, setTxMenu] = useState<{ x: number; y: number; row: ConsBusinessRow } | null>(null);
    const [txRow, setTxRow] = useState<ConsBusinessRow | null>(null);
    // Lost, and an Open that would drop a note or a reminder, are asked first (design audit
    // S1-04, Roman: "Rückfrage").
    const [confirmStatus, setConfirmStatus] = useState<{ row: ConsBusinessRow; next: CbStatus } | null>(null);

    // The review default rides on the stats response — the import/status endpoint it used to
    // come from went away with the legacy import (migration 132).
    const defaultReviewDays = stats?.defaultReviewDays ?? 90;

    const afterWrite = useCallback(() => {
        refresh();
        setDrawerRefreshKey(k => k + 1);
    }, [refresh]);

    const applyStatus = useCallback(async (row: ConsBusinessRow, payload: StatusSetPayload) => {
        setWriteBusy(true);
        try {
            const r = await consolidatedBusinessApi.setStatus(buildGroupKey(row), payload);
            toast(`${row.ProductName ?? 'Combination'} — ${CB_STATUS_META[r.status].label}`, 'success');
            setStatusModal(null);
            afterWrite();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to set status', 'error');
        } finally {
            setWriteBusy(false);
        }
    }, [toast, afterWrite]);

    // Checked / InProgress ask for a reminder (and a note). Lost, and an Open that would clear a
    // note or a reminder, ask a yes/no question; a plain Open is set outright.
    const pickStatus = useCallback((row: ConsBusinessRow, next: CbStatus) => {
        if (next === 'Checked' || next === 'InProgress') { setStatusModal({ row, target: next }); return; }
        const question = statusChangeQuestion(next, row.ProductName, {
            note: row.StatusNote, reviewDueAt: row.NextReviewDueAt,
        });
        if (question) setConfirmStatus({ row, next });
        else applyStatus(row, { status: next });
    }, [applyStatus]);

    const [drawerWidth, setDrawerWidth] = useState<number>(() => {
        if (typeof window === 'undefined') return DEFAULT_DRAWER_WIDTH;
        const stored = window.localStorage.getItem(DRAWER_WIDTH_KEY);
        const parsed = stored ? Number(stored) : NaN;
        return Number.isFinite(parsed) && parsed >= 40 && parsed <= 85 ? parsed : DEFAULT_DRAWER_WIDTH;
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(DRAWER_WIDTH_KEY, String(Math.round(drawerWidth)));
        }
    }, [drawerWidth]);

    if (permLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
        );
    }

    // A page the person cannot view is a refusal, not a broken page (design audit S2-10): it used
    // to render tiles, filters and an empty table under a red box, each failing on its own 403.
    if (!canView) {
        return (
            <div className="mx-auto mt-16 max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center">
                <Lock aria-hidden="true" className="mx-auto h-6 w-6 text-gray-500" />
                <h1 className="mt-3 text-base font-semibold text-gray-900">No access to Consolidated Business</h1>
                <p className="mt-1 text-sm text-gray-600">Ask an administrator for view access to this page.</p>
            </div>
        );
    }

    const totalPages = Math.ceil(total / limit);
    const drawerOpen = selectedRow !== null;
    const selectedKey = selectedRow ? rowKey(selectedRow) : null;

    // "Data as of" + stale banner (ADF does not run Fri/Sat → up to 48h old).
    const dataAsOf = stats?.dataAsOf ?? null;
    const isStale = (() => {
        if (!dataAsOf) return false;
        const asOf = new Date(dataAsOf).getTime();
        return Number.isFinite(asOf) && (Date.now() - asOf) > 48 * 3600 * 1000;
    })();

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Consolidated Business</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Customer × product combinations whose business is falling asleep or lost.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <KpiModeToggle value={kpiMode} onChange={setKpiMode} />
                    {dataAsOf && (
                        <span className="text-xs text-gray-500">
                            Data as of {fmtDate(dataAsOf)}
                        </span>
                    )}
                </div>
            </div>

            {isStale && (
                <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Data is more than 48 hours old — the source ETL does not run on Fridays and Saturdays.
                </div>
            )}

            {/* Stats — six clickable cards (D5); each one sets its own filter. */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                <ConsBusinessStatsBar stats={stats} filters={filters} onChange={setFilters} />
            </div>

            {/* Filters */}
            <ConsBusinessFiltersBar filters={filters} options={filterOptions} onChange={setFilters} />

            {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Table + Drawer split */}
            <div className="flex min-h-0 gap-3">
                <div className="relative min-w-0" style={{ width: drawerOpen ? `${100 - drawerWidth}%` : '100%' }}>
                    {loading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        </div>
                    )}
                    <ConsBusinessTable
                        items={items}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={setSort}
                        onSelect={openDrawer}
                        selectedKey={selectedKey}
                        compact={drawerOpen}
                        rowKey={rowKey}
                        kpiMode={kpiMode}
                        canEdit={canEdit}
                        onStatusClick={(row, e) => setStatusMenu({ x: e.clientX, y: e.clientY, row })}
                        onRowContextMenu={(row, e) => setTxMenu({ x: e.clientX, y: e.clientY, row })}
                    />
                    {/* A right-click-only feature is one the CFO demos once and nobody finds again. */}
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                        <MousePointerClick aria-hidden="true" className="h-3.5 w-3.5" />
                        Click a row (or Enter) for details · click the status icon to change it · right-click for the invoice history ·
                        drag a column edge to resize it, double-click the edge to reset.
                    </p>
                </div>
                {drawerOpen && selectedRow && (
                    <ConsBusinessDetailDrawer
                        groupKey={buildGroupKey(selectedRow)}
                        kpiMode={kpiMode}
                        onClose={closeDrawer}
                        width={drawerWidth}
                        onWidthChange={setDrawerWidth}
                        canEdit={canEdit}
                        defaultReviewDays={defaultReviewDays}
                        refreshKey={drawerRefreshKey}
                        onChanged={afterWrite}
                    />
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                    <p className="text-sm text-gray-600">
                        Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total.toLocaleString('de-CH')}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(page - 1)}
                            disabled={page <= 1}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ChevronLeft className="h-4 w-4" /> Prev
                        </button>
                        <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                        <button
                            onClick={() => setPage(page + 1)}
                            disabled={page >= totalPages}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {statusMenu && (
                <ContextMenu
                    x={statusMenu.x}
                    y={statusMenu.y}
                    onClose={() => setStatusMenu(null)}
                    items={CB_STATUS_ORDER.map(s => {
                        const meta = CB_STATUS_META[s];
                        const Icon = meta.icon;
                        const current = toCbStatus(statusMenu.row.Status) === s;
                        return {
                            label: current ? `${meta.label} (current)` : meta.label,
                            icon: <Icon className={`h-3.5 w-3.5 ${meta.iconClass}`} />,
                            disabled: current,
                            onClick: () => pickStatus(statusMenu.row, s),
                        };
                    })}
                />
            )}

            {statusModal && (
                <ConsBusinessStatusModal
                    target={statusModal.target}
                    productName={statusModal.row.ProductName}
                    defaultReviewDays={defaultReviewDays}
                    initialNote={statusModal.target === 'InProgress' ? statusModal.row.StatusNote : null}
                    noteToDrop={statusModal.target === 'Checked' ? statusModal.row.StatusNote : null}
                    busy={writeBusy}
                    onConfirm={payload => applyStatus(statusModal.row, payload)}
                    onCancel={() => setStatusModal(null)}
                />
            )}

            {confirmStatus && (() => {
                const q = statusChangeQuestion(confirmStatus.next, confirmStatus.row.ProductName, {
                    note: confirmStatus.row.StatusNote, reviewDueAt: confirmStatus.row.NextReviewDueAt,
                });
                return q && (
                    <ConfirmModal
                        title={q.title}
                        message={q.message}
                        confirmLabel={q.confirmLabel}
                        variant={q.variant}
                        onConfirm={() => { const c = confirmStatus; setConfirmStatus(null); applyStatus(c.row, { status: c.next }); }}
                        onCancel={() => setConfirmStatus(null)}
                    />
                );
            })()}

            {txMenu && (
                <ContextMenu
                    x={txMenu.x}
                    y={txMenu.y}
                    onClose={() => setTxMenu(null)}
                    items={[{
                        label: 'Transaction history',
                        icon: <Receipt className="h-3.5 w-3.5" />,
                        onClick: () => setTxRow(txMenu.row),
                    }]}
                />
            )}

            {txRow && (
                <ConsBusinessTransactionsModal
                    groupKey={buildGroupKey(txRow)}
                    productName={txRow.ProductName}
                    customerName={txRow.CustomerName}
                    customerId={txRow.CustomerID}
                    onClose={() => setTxRow(null)}
                />
            )}
        </div>
    );
}
